/**
 * Error Boundary Component
 * Catches React errors in the component tree and displays a fallback UI
 */

import React, { Component, ErrorInfo, ReactNode } from "react";
import ErrorFallback, { ErrorFallbackProps } from "./ErrorFallback";
import { logger } from "../../utils/logger";

/**
 * Get PostHog instance for error tracking
 */
function getPostHog(): unknown {
  try {
    if (typeof window !== "undefined") {
      const win = window as unknown as { posthog?: unknown };
      if (win.posthog) {
        return win.posthog;
      }
    }
  } catch {
    // Ignore errors
  }
  return null;
}

export interface ErrorBoundaryProps {
  children: ReactNode;
  /**
   * Fallback component to render on error
   */
  fallback?: React.ComponentType<ErrorFallbackProps>;
  /**
   * Callback when error occurs
   */
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
  /**
   * Reset error boundary when this prop changes
   */
  resetKeys?: Array<string | number>;
  /**
   * Custom title for error display
   */
  errorTitle?: string;
  /**
   * Custom message for error display
   */
  errorMessage?: string;
  /**
   * Show error details by default
   */
  showDetails?: boolean;
  /**
   * Additional recovery actions
   */
  actions?: ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
    };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return {
      hasError: true,
      error,
    };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // Log error using structured logger
    logger.error("ErrorBoundary caught an error", error, {
      componentStack: errorInfo.componentStack,
      errorBoundary: true,
    });

    // Send to PostHog for error tracking
    const posthog = getPostHog();
    if (posthog) {
      try {
        (
          posthog as {
            captureException: (
              error: Error,
              props?: Record<string, unknown>
            ) => void;
          }
        ).captureException(error, {
          $exception_message: error.message,
          $exception_type: error.constructor.name,
          componentStack: errorInfo.componentStack,
          errorBoundary: true,
        });
      } catch (err) {
        // If PostHog capture fails, log but don't break
        logger.warn("Failed to send error to PostHog from ErrorBoundary", {
          originalError: err,
        });
      }
    }

    // Call optional error callback
    if (this.props.onError) {
      this.props.onError(error, errorInfo);
    }
  }

  componentDidUpdate(prevProps: ErrorBoundaryProps) {
    // Reset error boundary when resetKeys change
    if (
      prevProps.resetKeys &&
      this.props.resetKeys &&
      prevProps.resetKeys.length > 0 &&
      this.props.resetKeys.some(
        (key, index) => prevProps.resetKeys![index] !== key
      )
    ) {
      this.resetErrorBoundary();
    }
  }

  resetErrorBoundary = () => {
    this.setState({
      hasError: false,
      error: null,
    });
  };

  render() {
    if (this.state.hasError && this.state.error) {
      const FallbackComponent = this.props.fallback || ErrorFallback;

      return (
        <FallbackComponent
          error={this.state.error}
          resetErrorBoundary={this.resetErrorBoundary}
          title={this.props.errorTitle}
          message={this.props.errorMessage}
          showDetails={this.props.showDetails}
          actions={this.props.actions}
        />
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
